import React from 'react';
import { Link } from 'react-router-dom';
import {
  SIGNAL_FIELDS,
  SIGNAL_PASSTHROUGH_FIELDS,
  SIGNAL_PASSTHROUGH_DAY_KEYS,
  SIGNAL_COLUMN_FILTER_ALIASES,
} from '@shared/flow/signal-view';
// `SIGNAL_FILTER_FIELDS` is composed in filter-fields.ts, not signal-view.ts
// — see signal-view.ts's own header comment on why (avoiding a real-value
// import cycle between the two modules).
import { SIGNAL_FILTER_FIELDS } from '@shared/flow/filter-fields';
import { effectiveSignalStatus, stepLabelFor } from '@shared/flow/signal-verification';
import type { FlowColumnDef, FlowSortOption, FlowTableConfig } from '../types';
import { EmDash, fmtDate, fmtDateOnly, money, score } from '../cells';
import { CompactUrlLink, DomainLinkCell, LinkedInCell } from '../links';
import { accountInCommandCenter } from '../../../../../../mini-apps/_shared/flow-links';
import { type PrioritizedSignalRow } from './prioritizeSignals';
import { parseCalendarDay } from '../../../lib/calendarDay';
import { dueTone, DUE_TONE_CLASS } from './dueTone';
import type { LocalTimeResult } from './localTime';
import { signalGroupValue, isBlank } from './signalGroupValue';
import { SignalDetail } from './SignalDetail';
import { SignalRowActions } from './SignalRowActions';

/**
 * The Signals table's `FlowTableConfig` (plan 2026-09-24-002 U5). Modelled
 * on `plays/playsTableConfig.tsx` — column KEYS come from `SIGNAL_FIELDS`
 * (the shared catalog, U2), and this file supplies only the three things
 * genuinely about Signals: how each column's cell reads, the due-date
 * color/staleness rule, and row expansion into the real contact card.
 */

/**
 * `SignalRow` plus the two client-computed columns attached in
 * `SignalsFlowTable.tsx`. Carries an index signature (harmless — every
 * declared field is a concrete subtype of `unknown`) so it satisfies
 * `arrangeSignalsClientSide`'s `ClientSortableSignalRow` constraint without a
 * cast at the call site.
 */
export interface SignalRowWithComputed extends PrioritizedSignalRow {
  local_time: LocalTimeResult;
  /** Day-level "Days on step" label (`computeStepSince`, Signals V3 PR 2 U4): "Today", "Nd" or "—". */
  step_since: string;
  /** Whole calendar days since `step_started_at`; `null` when there is no flow or the date is unusable. */
  step_since_days: number | null;
  /**
   * Set only on a `?signal=` deep-linked row the current view does not
   * contain (`useSignalTableModel`'s pin, plan 2026-10-13-001 U1) — the V2
   * name cell renders an "Opened from link — not in this view" chip off it.
   */
  pinned_from_link?: boolean;
  [key: string]: unknown;
}

/**
 * Exported (additive, S4-U3) so the V2 `shell-next/tables/signalsColumns.tsx`
 * due-date cell can reuse the exact same staleness threshold rather than a
 * second copy — behaviour-neutral for V1.
 */
export const GONG_STALE_AFTER_DAYS = 3;

/**
 * Days between `flowLastModifiedAt` and `today`, local-calendar-day —
 * null when either is absent/unparseable. Uses `prioritizeSignals.ts`'s
 * `parseCalendarDay` (the SAME local-midnight parsing its own overdue
 * tiebreak uses) rather than a second regex.
 *
 * Exported (additive, S4-U3) for the same reason as `GONG_STALE_AFTER_DAYS`
 * above.
 */
export function daysSinceGongUpdate(flowLastModifiedAt: string | null, today: Date): number | null {
  if (!flowLastModifiedAt) return null;
  const modified = parseCalendarDay(flowLastModifiedAt);
  if (!modified) return null;
  const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.round((todayStart.getTime() - modified.getTime()) / 86_400_000);
}


/** Generic neutral pill — the same treatment `plays/playsTableConfig.tsx`'s `play_type` column uses. */
function Pill({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium bg-bg-secondary dark:bg-nightshade-800 text-content-secondary dark:text-nightshade-400">
      {children}
    </span>
  );
}

const LOCAL_TIME_TONE_CLASS: Record<LocalTimeResult['status'], string> = {
  open: 'text-content-success dark:text-matcha-400',
  closed: 'text-content-danger dark:text-red-400',
  unknown: 'text-content-faint dark:text-nightshade-600',
};

function LocalTimeBadge({ localTime }: { localTime: LocalTimeResult }) {
  if (localTime.status === 'unknown' || localTime.label == null) return <EmDash />;
  return <span className={`font-medium ${LOCAL_TIME_TONE_CLASS[localTime.status]}`}>{localTime.label}</span>;
}

/*
 * QA batch 1, D-E: 93.7% of open To-do rows have a NULL `signal_tactic` on
 * prod-mirror, so grouping by it put most of a rep's assignees under a
 * generic `— (n)` header (`step_number` is the same defect class — My
 * Signals groups by it and 93.7% of open rows are also null there). FlowTable
 * routes every grouping computation (counts, header boundaries, collapse
 * keys) through `config.groupValue`, so the label and the group IDENTITY
 * change together. QA batch 2, D-J: both null-tactic and null-step-number
 * step-number groups use Action View's `NO_STEP_DATA_LABEL` ("No step data"); null-tactic ("Step type") groups read "No step type".
 * QA batch 3, D-C: this widened into a full parity module,
 * `./signalGroupValue.ts` — EVERY groupable Signals key now has an Action
 * View empty-group label (`SIGNAL_GROUP_EMPTY_LABELS`,
 * `src/shared/flow/signal-group-labels.ts`) — including the ten Terra-only
 * picker keys (fix-plan-batch3.md D-F) — plus the `step_number`/
 * `flow_name`/`local_time`/`business_hours` real-value transforms. `isBlank`
 * moved there too (the contact_location renderer below reuses it via the
 * same import — the identical "no real value" test both need).
 */

/**
 * Signals V3 PR 1, U3 — the generated Contact/Account passthrough columns read
 * by TYPE (dates, dollars, scores) with the SAME helpers `contactColumns.tsx`
 * uses, so a field reads identically on People, Companies and Signals. Text,
 * picklist and boolean fall through to FlowTable's default cell (text as-is,
 * booleans Yes/No). A calendar DATE (`SIGNAL_PASSTHROUGH_DAY_KEYS`) uses
 * `fmtDateOnly`, an instant uses `fmtDate` — the two are not interchangeable
 * (see `cells.tsx`). Dollar columns are the Account money fields.
 */
const PASSTHROUGH_BY_KEY = new Map(SIGNAL_PASSTHROUGH_FIELDS.map(f => [f.key, f]));
const MONEY_PASSTHROUGH_KEYS = new Set([
  'account_arr', 'account_open_pipeline', 'account_arr_opportunity', 'account_pcv', 'account_gs_arr',
]);

function passthroughRendererFor(key: string): ((row: SignalRowWithComputed) => React.ReactNode) | undefined {
  const field = PASSTHROUGH_BY_KEY.get(key);
  if (!field) return undefined;
  if (field.type === 'date') {
    const format = SIGNAL_PASSTHROUGH_DAY_KEYS.has(key) ? fmtDateOnly : fmtDate;
    return row => {
      const v = row[key];
      return format(typeof v === 'string' ? v : null);
    };
  }
  if (field.type === 'number') {
    const format = MONEY_PASSTHROUGH_KEYS.has(key) ? money : score;
    return row => {
      const v = row[key];
      return format(typeof v === 'number' ? v : null);
    };
  }
  return undefined;
}

/** Every `SIGNAL_FIELDS` key needing a non-default cell. `today` closes over the due-date/staleness comparison — see `buildSignalsTableConfig`'s own doc comment for why it's an explicit param rather than a hidden clock read. */
function rendererFor(key: string, today: Date): ((row: SignalRowWithComputed) => React.ReactNode) | undefined {
  switch (key) {
    case 'contact_name':
      return row => (
        <span className="font-semibold text-content-primary dark:text-nightshade-100">{row.contact_name ?? '—'}</span>
      );
    case 'account_name':
      return row =>
        row.account_id ? (
          <Link
            to={accountInCommandCenter(row.account_id)}
            onClick={e => e.stopPropagation()}
            className="text-content-action dark:text-blueberry-400 hover:underline"
          >
            {row.account_name ?? '—'}
          </Link>
        ) : (
          (row.account_name ?? <EmDash />)
        );
    case 'account_sales_tier':
      return row => (row.account_sales_tier ? <Pill>{row.account_sales_tier}</Pill> : <EmDash />);
    // QA batch 2, D-C: the Step cell shows the verb-mapped label when one
    // exists, else the raw tactic text, else an em dash — NEVER a fallback
    // verb string ("Open in Gong" no longer exists anywhere, D-A). This is
    // display only — whether the row's ACTION button shows a verb is gated
    // by verification state (`getSignalRowActions`, `SignalRowActions.tsx`),
    // a separate, stricter question this cell doesn't answer.
    case 'signal_tactic':
      return row => <span>{stepLabelFor(row.signal_tactic) ?? row.signal_tactic ?? <EmDash />}</span>;
    case 'due_date':
      return row => {
        const tone = dueTone(row.due_date, today);
        const staleDays = daysSinceGongUpdate(row.flow_last_modified_at, today);
        const stale = staleDays != null && staleDays > GONG_STALE_AFTER_DAYS;
        return (
          <span className={`inline-flex items-center gap-1 ${tone ? DUE_TONE_CLASS[tone] : ''}`}>
            {/* `fmtDateOnly`, not `fmtDate` (round-1 fix D-K): `due_date` is
                the one Postgres DATE column here — Prisma hands it over as
                `…T00:00:00.000Z`, and the instant-aware formatter turned
                that into the PREVIOUS day for every US rep, disagreeing
                with the `dueTone` colour computed right above it. Every
                other date column in this config is a timestamptz and keeps
                `fmtDate`. */}
            {fmtDateOnly(row.due_date)}
            {stale && (
              <span
                role="img"
                aria-label={`Gong data last updated ${staleDays} days ago`}
                title={`Gong data last updated ${staleDays} days ago`}
              >
                ⚠
              </span>
            )}
          </span>
        );
      };
    // Coordinator addition to fix-plan-batch2.md unit U4: the pill shows
    // the SAME effective status the row's buttons/chip already resolve on
    // (`effectiveSignalStatus` — the probe's own verdict when a fresh
    // observation exists, else the snapshot), so a row never reads
    // "Running" here while its actions already show Archive only. Display
    // only — filtering/sorting/grouping stay on the raw `signal_status`
    // snapshot column (server-side, unchanged).
    case 'signal_status':
      return row => {
        const status = effectiveSignalStatus(row);
        return status ? <Pill>{status}</Pill> : <EmDash />;
      };
    case 'local_time':
      return row => <LocalTimeBadge localTime={row.local_time} />;
    // U6 — a Postgres DATE (calendar day), never an instant: `fmtDateOnly`, like `due_date`.
    case 'step_date':
      return row => fmtDateOnly(row.step_date as string | null | undefined);
    case 'flow_enrolled_at':
    case 'event_date':
    case 'snooze_until':
    case 'action_completed_at':
      return row => fmtDate(row[key]);
    case 'source_link':
      return row => <CompactUrlLink url={row.source_link} label="Open" />;
    // QA batch 1, D-C: client-computed from the three contact_mailing_*
    // fields the row already carries — the Accounts `hq` pattern
    // (AccountFlowTable.tsx's `hq` renderer), not a real wire field.
    case 'contact_location':
      return row => {
        const parts = [row.contact_mailing_city, row.contact_mailing_state, row.contact_mailing_country]
          .filter((v): v is string => !isBlank(v));
        return parts.length ? <span title={parts.join(', ')}>{parts.join(', ')}</span> : <EmDash />;
      };
    // QA batch 1, D-C/D-F: shared LinkedIn pill / domain-link cells
    // (links.tsx) — the same cells ContactFlowTable/AccountFlowTable use.
    case 'contact_linkedin':
      return row => <LinkedInCell url={row.contact_linkedin} />;
    case 'account_linkedin':
      return row => <LinkedInCell url={row.account_linkedin} />;
    case 'account_email_domain_link':
      return row => <DomainLinkCell raw={row.account_email_domain_link} />;
    default:
      return passthroughRendererFor(key);
  }
}

/** Columns whose values are best right-aligned/tabular (mirrors `ContactFlowTable.tsx`'s `NUMERIC_KEYS`). */
const NUMERIC_KEYS = new Set(['step_number', 'days_open']);

/**
 * `step_number` opts BACK IN to the Group-by picker (R5) — `FlowTable`'s
 * derivation excludes every `number`-typed column by default. `local_time`
 * (QA batch 3, D-A/D-B) opts back in for the SAME reason it's excluded from
 * the derivation in the first place: it's `noFilter` on `SIGNAL_FIELDS` (a
 * client-computed column the server can't filter on), and the derivation
 * treats `noFilter` as "not groupable" too — grouping by it is entirely
 * client-side (`arrangeSignalsClientSide`, G3), same as sorting by it
 * already is (`SORTABLE_SYNTHETIC_KEYS` below).
 */
const GROUPABLE_KEYS = new Set(['step_number', 'local_time']);

/**
 * R11 — the client-computed column the tab sorts by (`local_time`; `priority`
 * left the column set in Signals V3 PR 1 U5 but still sorts via
 * `SIGNAL_SORT_OPTIONS`) had a header that did nothing. It is `noFilter` on `SIGNAL_FIELDS` (the server
 * cannot filter a client-computed column), and `FlowTable`'s header-click
 * guard reads `noFilter` as "unsortable" too. `FlowColumnDef.sortable` is
 * the explicit opt-in that overrides that derivation (see its own doc
 * comment in `../types.ts`); sorting them is honoured entirely in the
 * browser (`arrangeSignalsClientSide`, D10) and `useSignalsQueries`'s
 * `serverSortFor` keeps either name off the wire.
 */
const SORTABLE_SYNTHETIC_KEYS = new Set(['local_time']);

/**
 * QA batch 2, D-B/A: the Signals-tab Sort control's options, in Action
 * View's own order and semantics (`useActionViewState.ts:245-273`). Signals
 * is the first `sortOptions` consumer (`types.ts`'s own doc comment) — every
 * other mount leaves `config.sortOptions` undefined and gets no picker.
 * `priority`/`local_time` stay client-sorted within server groups
 * (`isClientSort`, `SignalsFlowTable.tsx`); `days_open`/`due_date` are real
 * server sort keys. The two `local_time` entries share a `key` and differ
 * only by `dir` — `FlowTable`'s `SortPicker` matches on `(key, dir)` together
 * so they render as two distinct, individually-selectable options.
 */
const SIGNAL_SORT_OPTIONS: FlowSortOption[] = [
  // Signals V3 PR 1, U5 — the ranking itself, no longer a visible number column; "Suggested order" is the rep-facing name.
  { key: 'priority', label: 'Suggested order', dir: 'ASC' },
  { key: 'days_open', label: 'Time Open', dir: 'DESC' },
  { key: 'due_date', label: 'Due Date', dir: 'ASC' },
  { key: 'local_time', label: 'Local Time ↑', dir: 'ASC' },
  { key: 'local_time', label: 'Local Time ↓', dir: 'DESC' },
];

function buildColumns(today: Date): FlowColumnDef<SignalRowWithComputed>[] {
  return SIGNAL_FIELDS.map(field => ({
    key: field.key,
    label: field.label,
    type: field.type,
    noFilter: field.noFilter,
    sortable: SORTABLE_SYNTHETIC_KEYS.has(field.key) || undefined,
    groupable: GROUPABLE_KEYS.has(field.key) || undefined,
    numeric: NUMERIC_KEYS.has(field.key) || field.type === 'number' || undefined,
    render: rendererFor(field.key, today),
  }));
}

export interface SignalsTableConfigOptions {
  /** Fires once a Snooze/Bring back/Archive mutation completes — drives auto-expand-next (bead .16). */
  onActed?: (signalId: string) => void;
  /** A row/Timeline Archive click (`SignalRowActions.tsx`) — routes to the Signal card's inline archive form (QA batch 2, D-I; B8 renders the card). */
  onArchiveRequest?: (signalId: string) => void;
  /**
   * The verification-freshness clock passed to `getSignalRowActions`
   * (`SignalRowActions.tsx`'s `now` prop, D-A). Defaults to `today` so
   * renderer-only tests (`signalsTableConfig.test.tsx`'s due-date/Step-cell
   * suites) don't need to pass a second, near-identical clock — `today` is
   * midnight-granular (`useLocalDayToken`) while `now` is minute-fresh
   * (`useMinuteToken`), which only matters for the freshness math itself,
   * not for any test that never renders `rowAction`.
   */
  now?: Date;
  /** View as (U15): the row's write buttons render disabled. Only the V2 mount can set it. */
  readOnly?: boolean;
}

/**
 * `today` is an explicit param (not `new Date()` read inside a renderer)
 * so the due-date color/staleness rule is pure and testable without faking
 * the system clock — the same discipline `prioritizeSignals`/
 * `arrangeSignalsClientSide` already follow. `SignalsFlowTable.tsx` supplies
 * it from its own `useLocalDayToken`-derived value, the single source of
 * "today" shared with the Ranker's overdue tiebreak. `now` (QA batch 2, D-A)
 * is a SEPARATE, minute-fresh clock `SignalsFlowTable.tsx` already computes
 * for `localTimeFor` (`useMinuteToken`) — reused here rather than adding a
 * third clock read.
 */
export function buildSignalsTableConfig(
  today: Date,
  { onActed = () => {}, onArchiveRequest, now = today, readOnly = false }: SignalsTableConfigOptions = {},
): FlowTableConfig<SignalRowWithComputed> {
  return {
    columns: buildColumns(today),
    filterFields: SIGNAL_FILTER_FIELDS,
    filterFieldAliases: SIGNAL_COLUMN_FILTER_ALIASES,
    rowKey: row => row.signal_id,
    renderDetail: row => <SignalDetail row={row} onActed={onActed} />,
    // The omnibox (Command Center's header) handles search on this tab —
    // same reasoning as the global Contacts/Accounts mounts.
    showSearch: false,
    rowAction: row => <SignalRowActions row={row} now={now} onActed={onActed} onArchiveRequest={onArchiveRequest} disabled={readOnly} />,
    groupValue: signalGroupValue,
    // QA batch 1, D-A: Signals reads as a task list of sections, not a
    // spreadsheet — Contacts/Accounts/Plays keep the default 'compact'
    // grouping density (their Tracker/persona headers were QA'd dense).
    groupHeaderStyle: 'section',
    sortOptions: SIGNAL_SORT_OPTIONS,
    // QA batch 3, D-D: Business hours has no column at all (the Local time
    // badge already renders the status by colour — no invented column, UX
    // map §4a) — a group-only key, appended to `groupableColumns` beside
    // FlowTable's own `TRACKER_GROUP_KEY` (`FlowTable.tsx`, G2).
    groupOnlyKeys: [{ key: 'business_hours', label: 'Business hours' }],
  };
}
